import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Location } from 'react-router-dom';
import { pageRoutes } from '../app-routes';

export type OpenPage = {
  key: string;
  title: string;
  location: Location;
};

type OpenPagesContextValue = {
  pages: OpenPage[];
  activeKey: string;
  activatePage: (page: OpenPage) => void;
  closePage: (page: OpenPage) => void;
};

const OpenPagesContext = createContext<OpenPagesContextValue>({
  pages: [],
  activeKey: '',
  activatePage: () => undefined,
  closePage: () => undefined,
});

const getPageKey = (pathname: string, search: string) => {
  const id = new URLSearchParams(search).get('id');
  return id ? `${pathname}?id=${id}` : pathname;
};

const routesWithoutOpenPage = new Set(['/overview', '/analytics', '/planning']);

export const OpenPagesProvider = ({ children }: React.PropsWithChildren) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [pages, setPages] = useState<OpenPage[]>([]);
  const activeKey = getPageKey(location.pathname, location.search);

  useEffect(() => {
    if (routesWithoutOpenPage.has(location.pathname)) return;
    const route = pageRoutes.find(item => item.path === location.pathname);
    if (!route) return;

    const page: OpenPage = {
      key: getPageKey(location.pathname, location.search),
      title: new URLSearchParams(location.search).get('id')
        ? `${route.title} #${new URLSearchParams(location.search).get('id')}`
        : route.title,
      location,
    };

    setPages(current => {
      const existingIndex = current.findIndex(item => item.key === page.key);
      if (existingIndex === -1) return [...current, page];

      const updated = [...current];
      updated[existingIndex] = page;
      return updated;
    });
  }, [location]);

  const activatePage = useCallback((page: OpenPage) => {
    navigate(`${page.location.pathname}${page.location.search}${page.location.hash}`);
  }, [navigate]);

  const closePage = useCallback((page: OpenPage) => {
    const index = pages.findIndex(item => item.key === page.key);
    if (index === -1) return;

    const remaining = pages.filter(item => item.key !== page.key);
    setPages(remaining);
    if (page.key === activeKey) {
      const nextPage = remaining[Math.max(0, index - 1)] ?? remaining[0];
      const fallbackPath = pageRoutes.find(item => item.path === '/overview')?.path;
      const target = nextPage
        ? `${nextPage.location.pathname}${nextPage.location.search}${nextPage.location.hash}`
        : fallbackPath;
      if (target) navigate(target, { replace: true });
    }
  }, [activeKey, navigate, pages]);

  const value = useMemo(() => ({ pages, activeKey, activatePage, closePage }), [pages, activeKey, activatePage, closePage]);
  return <OpenPagesContext.Provider value={value}>{children}</OpenPagesContext.Provider>;
};

export const useOpenPages = () => useContext(OpenPagesContext);
